import {
  GET_ARTICLES_DETAIL,
  GET_ARTICLES_PREVIEW,
  GET_ARTICLES_REDACTION,
  GET_ARTICLES_WITH_AUTHOR
} from "~/graphql/articles"
import type {Article} from "~/graphql/__generated__/graphql"
import type ArticleLocal from "~/libs/interfaces/ArticleLocal"
import articleLocalFactory from "~/libs/factories/ArticleLocalFactory"
import {usePersistedStore} from "~/stores/usePersistedStore"
import type {Ref} from "vue";
import {ArticleStoreType} from "~/libs/constants/ArticleStoreType"
import type ArticleFilters from "~/libs/interfaces/ArticleFilters"
import NotFoundError from "~/libs/errors/NotFoundError"
import {useStaticMenuConfig} from "~/composables/useStaticMenuConfig"
import {MenuHeaderConfig} from "~/config/MenuHeaderConfig"
import {useApiIdConverter} from "~/composables/useApiIdConverter"
import {useForbiddenCategoryIds} from "~/composables/useForbiddenCategoryIds"
import ApolloResponseError from "~/libs/errors/ApolloResponseError"
import {ApolloError} from "@apollo/client"

export enum ARTICLE_QUERY_TYPE {
  PREVIEW = 'preview',
  WITH_AUTHOR = 'with_author',
  DETAIL = 'detail',
  REDACTION = 'redaction'
}

export interface FetchArticlesOptions {
  forceRefetch?: boolean
  useDefaultFilters?: boolean
  usePublishedStateFilters?: boolean
  checkFiltersChange?: boolean
}

const articleStore = (type: ArticleStoreType = ArticleStoreType.HOMEPAGE, key?: string) => defineStore(`articles-${type}-${key}`, {
  state: () => {
    const state: {
      isLoading: Ref<boolean>
      limit: Ref<number>
      articles: ArticleLocal[]
      article: Ref<ArticleLocal | null>
      filters: ArticleFilters
    } = {
      isLoading: ref(true),
      limit: ref(10),
      articles: reactive([]),
      article: ref(null),
      filters: {},
    }
    return state
  },
  getters: {
    getIsLoading(state): boolean {
      return state.isLoading
    }
  },
  actions: {
    setLimit(limit: number) {
      this.limit = limit
    },
    /**
     * Get articles with applied filters and presets
     * @param offset
     * @param limit
     * @return ArticleLocal[]
     */
    getFilteredArticles(offset: number = 0, limit: number = 10): ArticleLocal[] {
      let matchedCount = 0
      const forbiddenCategoryIds = useForbiddenCategoryIds().getForbiddenCategoryIds()

      const articles = this.articles.map((article) => {
        let matchFilters = true

        if (useArticleCategoryIdSearch(false, article, forbiddenCategoryIds)) {
          matchFilters = false
        }

        if (matchFilters) {
          matchedCount = matchedCount + 1
        }

        if (matchFilters && matchedCount <= offset) {
          matchFilters = false
        } else if (matchFilters && matchedCount > (offset + limit)) {
          matchFilters = false
        }

        return {
          ...article,
          isVisible: matchFilters
        }
      })
      return articles
    },

    /**
     * Get only filtered articles (remove non visible)
     * @param offset
     * @param limit
     */
    getOnlyFilteredArticles(offset: number = 0, limit: number = 10): ArticleLocal[] {
      const articles = this.getFilteredArticles(offset, limit)
      return articles.filter((article) => article.isVisible)
    },

    /**
     * Get count of getFilteredArticles
     * @return number
     */
    getFilteredArticlesCount(): number {
      const articles = this.getFilteredArticles(0, this.articles.length).filter(article => article.isVisible)
      return articles?.length || 0
    },

    /**
     * Get count of articles
     * @return number
     */
    getArticlesCount(): number {
      return this.articles.length || 0
    },
    /**
     * Get article by articleId
     * @param id
     */
    getArticle(id: string): ArticleLocal | undefined {
      const article = this.articles.find((article) => {
        return article.id.indexOf(id) !== -1
      })
      return article
    },
    /**
     * Get article by path
     * @param path
     */
    getArticleByPath(path: string): ArticleLocal | undefined {
      const article = this.articles.find((article) => {
        return article.path?.indexOf(path) !== -1
      })
      return article
    },
    /**
     * Fetch filter changed
     * @param filters
     */
    filterChanged(filters?: ArticleFilters): boolean {
      const sameArrays = (a?: string[], b?: string[]) => {
        if ((!a && b) || (a && !b)) {
          return false
        }
        if (!a && !b) {
          return true
        }
        if (a === b) {
          return true
        }
        if (a && b) {
          if (a.length !== b.length) {
            return false
          }
          const sortedA = a.sort()
          const sortedB = b.sort()

          for (let i = 0; i < sortedA.length; i++) {
            if (sortedA[i] !== sortedB[i]) {
              return false
            }
          }
        }
        return true
      }

      // check item ids filter
      if (!sameArrays(filters?.ids, this.filters.ids)) {
        return true
      }

      // check category filter
      if (!sameArrays(filters?.categoryIds, this.filters.categoryIds)) {
        return true
      }

      // check keyword filter
      if (!sameArrays(filters?.keywordIds, this.filters.keywordIds)) {
        return true
      }

      // check magazine filter
      if (!sameArrays(filters?.magazineIds, this.filters.magazineIds)) {
        return true
      }

      // check author filter
      if (!sameArrays(filters?.authorIds, this.filters.authorIds)) {
        return true
      }

      // check path filter
      if (!sameArrays(filters?.paths, this.filters.paths)) {
        return true
      }

      // check fulltext
      if (filters?.fulltext !== this.filters.fulltext) {
        return true
      }

      return false
    },
    checkFiltersChanges(filters: ArticleFilters): void {
      // reset articles if filter changed
      if (this.filterChanged(filters)) {
        this.filters.ids = filters.ids
        this.filters.categoryIds = filters.categoryIds
        this.filters.keywordIds = filters.keywordIds
        this.filters.magazineIds = filters.magazineIds
        this.filters.authorIds = filters.authorIds
        this.filters.fulltext = filters.fulltext
        this.filters.paths = filters.paths
        this.articles = reactive([])
        this.article = null
      }
    },

    /**
     * Fetch loaded articles details
     * @param queryType
     */
    async fetchDetailsForArticles(queryType:ARTICLE_QUERY_TYPE = ARTICLE_QUERY_TYPE.DETAIL): Promise<void> {
      if (!this.articles || this.articles.length === 0) {
        return
      }
      const apiIdConverter = useApiIdConverter()
      const filters: ArticleFilters = {
        ids: this.articles.map(article => apiIdConverter.apiIdToUuid(article.id)).filter(item => !!item) as string[]
      }
      return this.fetchArticles(filters, queryType, {
        forceRefetch: true,
        useDefaultFilters: false,
        usePublishedStateFilters: false,
        checkFiltersChange: false,
      })
    },

    /**
     * Fetch articles from server
     * @return {Promise<void>}
     */
    async fetchArticles(
      filters: ArticleFilters = {},
      queryType:ARTICLE_QUERY_TYPE = ARTICLE_QUERY_TYPE.PREVIEW,
      options: FetchArticlesOptions = {}
    ): Promise<void> {
      const logger = useFrontendLogger()
      const config = useRuntimeConfig()
      const apolloContext = {
        fetchOptions: { signal: AbortSignal.timeout(Number(config.public.articlesStoreTimeout || 5000)) }
      }
      const persistedStore = usePersistedStore()
      const staticMenuConfig = useStaticMenuConfig(MenuHeaderConfig)
      const settings = {
        forceRefetch: false,
        useDefaultFilters: true,
        usePublishedStateFilters: true,
        checkFiltersChange: true,
        ...options
      }

      let query = GET_ARTICLES_PREVIEW
      switch (queryType) {
        case ARTICLE_QUERY_TYPE.DETAIL:
          query = GET_ARTICLES_DETAIL
          break
        case ARTICLE_QUERY_TYPE.REDACTION:
          query = GET_ARTICLES_REDACTION
          break
        case ARTICLE_QUERY_TYPE.WITH_AUTHOR:
          query = GET_ARTICLES_WITH_AUTHOR
          break
        case ARTICLE_QUERY_TYPE.PREVIEW:
        default:
          query = GET_ARTICLES_PREVIEW
          break
      }

      if (settings.useDefaultFilters) {
        // set default filters for web
        filters = staticMenuConfig.mergeFilters(
          staticMenuConfig.getDefaultFilters(),
          filters,
          true
        )
      }

      // reset articles if filter changed
      if (settings.checkFiltersChange) {
        this.checkFiltersChanges(filters)
      }

      // no need to fetch articles
      if (!settings.forceRefetch && this.articles.length >= this.limit && !persistedStore.needSync(this.$id)) {
        return
      }

      this.isLoading = true

      const articlesFilterDateTimeOffsetMinutes = config.public.articlesFilterDateTimeOffsetMinutes ? Number(config.public.articlesFilterDateTimeOffsetMinutes) : 0
      const publishedDate = useDate().addMinutes(new Date(), articlesFilterDateTimeOffsetMinutes)

      const filter = {
        first: this.limit,
        // default publish state filters
        ...(settings.usePublishedStateFilters && {
          isDeleted: false,
          isCompleted: true,
          isPreview: false,
          unpublished: [{
            after: publishedDate.toISOString()
          }],
          published: [{
            before: publishedDate.toISOString()
          }],
        }),
        ...(filters && {
          // ids
          ...(filters.ids?.length && {
            idList: filters.ids
          }),
          // magazines
          ...(filters.magazineIds?.length && {
            magazineList: filters.magazineIds
          }),
          // categories
          ...(filters.categoryIds?.length && {
            categoryListList: filters.categoryIds
          }),
          // keywords
          ...(filters.keywordIds?.length && {
            keywordListList: filters.keywordIds
          }),
          // authors
          ...(filters.authorIds?.length && {
            authorListList: filters.authorIds
          }),
          // path
          ...(filters.paths?.length && {
            pathList: filters.paths
          }),
          // fulltext
          ...(filters.fulltext && filters.fulltext.length && {
            title: filters.fulltext,
            perex: filters.fulltext,
            body: filters.fulltext
          })
        })
      }

      // SSR server side initial data fetch
      if (import.meta.server) {
        try {

          const result = await useAsyncQuery(query, filter, undefined, apolloContext)
          if (result.error?.value) {
            throw new ApolloResponseError(result.error?.value?.message, result.error?.value)
          } else {
            //@ts-ignore
            const edges = result.data?.value?.articles?.edges || []
            this.processArticlesData(edges, this.limit === 1)
            persistedStore.setLastSync(this.$id)
          }
          this.isLoading = false
        } catch (e) {
          logger?.error('useArticlesStore:fetchArticles:server - exception', {
            errorDetail: e,
            getArticlesQueryType: queryType,
            getArticlesFilter: filter,
          })
          this.isLoading = false
          throw e
        }
      } else {
        // client interaction data fetch
        return new Promise((resolve, reject) => {
          try {
            const options = {
              context: apolloContext
            }
            const {onResult, onError, stop} = useQuery(query, filter, options)
            onResult((result) => {
              if (!result.loading) {
                stop()
                this.isLoading = false
                persistedStore.setLastSync(this.$id)
                const edges = result.data?.articles?.edges || []
                try {
                  this.processArticlesData(edges, this.limit === 1)
                  resolve()
                } catch (e) {
                  this.isLoading = false
                  logger?.error('useArticlesStore:fetchArticles:client - processArticlesData exception', {
                    errorDetail: e,
                    getArticlesQueryType: queryType,
                    getArticlesFilter: filter,
                  })
                  reject(e)
                }
              }
            })
            onError((error: ApolloError) => {
              this.isLoading = false
              logger?.error('useArticlesStore:fetchArticles:client - error', {
                errorDetail: error,
                getArticlesQueryType: queryType,
                getArticlesFilter: filter,
              })
              reject(new ApolloResponseError(error.message, error))
            })
          } catch (e) {
            this.isLoading = false
            logger?.error('useArticlesStore:fetchArticles:client - exception', {
              errorDetail: e,
              getArticlesQueryType: queryType,
              getArticlesFilter: filter,
            })
            reject(e)
          }
        })
      }
    },
    /**
     * Process loaded articles from GQL
     * @param data
     * @param isDetail
     */
    processArticlesData(data: any[], isDetail: boolean = false):void {
      if (data.length) {
        const newArticles: Article[] = data.map((item) => item?.node).filter(Boolean)
        // check article detail load
        if (isDetail && newArticles[0]) {
          this.setArticle(newArticles[0])
        } else if (isDetail && (!newArticles || !newArticles.length)) {
          throw new NotFoundError('Article not found')
        }
        if (newArticles && newArticles.length) {
          // add new articles to article list
          this.addArticles(newArticles, true)
        }
      } else if (isDetail) {
        throw new NotFoundError('Article not found')
      }
    },
    /**
     * Add articles to store - remove duplicities
     * @param newArticles
     * @param rewrite
     */
    addArticles(newArticles: Article[], rewrite: boolean = false): void {
      newArticles.forEach((newArticle) => {
        const articleLocal = articleLocalFactory.createArticleLocalFromArticle(newArticle)

        const foundIndex = this.articles.findIndex(article => article.id === newArticle.id)
        if (foundIndex >= 0 && rewrite) {
          this.articles.splice(foundIndex, 1)
        }
        if (foundIndex === -1 || rewrite) {
          this.articles.push(articleLocal)
        }
      })

      // sort articles
      this.articles.sort((a, b) => {
        if (a.publishedDate && b.publishedDate) {
          return a.publishedDate?.getTime() > b?.publishedDate?.getTime() ? -1 : 1
        }
        return 1
      })
    },

    setArticle(newArticle: Article): void {
      this.article = articleLocalFactory.createArticleLocalFromArticle(newArticle)
    }

  }
})

export const useArticlesStore = (type: ArticleStoreType, key?: string) => {
  return articleStore(type, key)()
}
